import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Shield, Lock, Eye, Database } from "lucide-react";
import ClientBackground from "../components/ClientBackground";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  const highlights = [
    {
      icon: Shield,
      title: "Your Data is Protected",
      description: "Industry-standard encryption and security measures"
    },
    {
      icon: Lock,
      title: "No Data Selling",
      description: "We never sell your personal information to third parties"
    },
    {
      icon: Eye,
      title: "Transparent Practices",
      description: "Clear information about what data we collect and why"
    },
    {
      icon: Database,
      title: "You're in Control",
      description: "Access, download, or delete your data anytime"
    }
  ];

  const sections = [
    {
      title: "1. Information We Collect",
      content: [
        "Personal Information: Name, email address, phone number, and profile information you provide during registration.",
        "Child Information: Child's name, date of birth, developmental milestones, and health-related information you choose to track.",
        "Usage Data: How you interact with our app, including features used, time spent, and navigation patterns.",
        "Device Information: Device type, operating system, browser type, and IP address.",
        "Cookies and Similar Technologies: We use cookies to improve your experience and analyze app usage."
      ]
    },
    {
      title: "2. How We Use Your Information",
      content: [
        "To provide and improve our services, including personalized milestone tracking and recommendations.",
        "To communicate with you about your account, updates, and new features.",
        "To enable community features and connect you with other parents.",
        "To analyze usage patterns and improve app functionality.",
        "To comply with legal obligations and protect our rights.",
        "To send you marketing communications (with your consent)."
      ]
    },
    {
      title: "3. Information Sharing",
      content: [
        "Service Providers: We share data with trusted third-party service providers who help us operate our app (e.g., hosting, analytics, payment processing).",
        "Community Features: Information you post in community forums is visible to other users.",
        "Legal Requirements: We may disclose information when required by law or to protect our rights.",
        "Business Transfers: In case of merger, acquisition, or sale, your information may be transferred.",
        "We do NOT sell your personal information to advertisers or other third parties."
      ]
    },
    {
      title: "4. Data Security",
      content: [
        "We implement industry-standard security measures including encryption, secure servers, and access controls.",
        "All data transmission is encrypted using SSL/TLS protocols.",
        "Access to personal information is restricted to authorized personnel only.",
        "We regularly review and update our security practices.",
        "However, no system is 100% secure, and we cannot guarantee absolute security."
      ]
    },
    {
      title: "5. Your Rights and Choices",
      content: [
        "Access: Request a copy of your personal data.",
        "Correction: Update or correct inaccurate information.",
        "Deletion: Request deletion of your account and personal data.",
        "Opt-Out: Unsubscribe from marketing emails at any time.",
        "Data Portability: Download your data in a portable format.",
        "Cookie Settings: Manage cookie preferences in your browser settings."
      ]
    },
    {
      title: "6. Children's Privacy",
      content: [
        "TIFL is designed for parents and caregivers, not children.",
        "We do not knowingly collect information from children under 13.",
        "Child profile information is controlled by the parent/guardian.",
        "Parents have full control over their child's data and can delete it at any time."
      ]
    },
    {
      title: "7. Data Retention",
      content: [
        "We retain your information as long as your account is active.",
        "After account deletion, we may retain certain data for legal compliance (e.g., transaction records).",
        "Anonymous usage data may be retained for analytics purposes.",
        "You can request complete data deletion by contacting support."
      ]
    },
    {
      title: "8. International Data Transfers",
      content: [
        "Your information may be transferred to and processed in countries other than your own.",
        "We ensure appropriate safeguards are in place for international transfers.",
        "By using TIFL, you consent to such transfers."
      ]
    },
    {
      title: "9. Third-Party Links",
      content: [
        "Our app may contain links to third-party websites or services.",
        "We are not responsible for the privacy practices of these third parties.",
        "We encourage you to review their privacy policies before providing any information."
      ]
    },
    {
      title: "10. Updates to This Policy",
      content: [
        "We may update this Privacy Policy from time to time.",
        "Changes will be posted on this page with an updated 'Last Updated' date.",
        "We will notify you of significant changes via email or app notification.",
        "Continued use after changes constitutes acceptance of the updated policy."
      ]
    },
    {
      title: "11. Contact Us",
      content: [
        "If you have questions or concerns about this Privacy Policy:",
        "Email: privacy@tifl.com",
        "Address: TIFL Privacy Team, [Your Address]",
        "We will respond to your inquiry within 30 days."
      ]
    }
  ];

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10">
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg font-semibold text-[#1e3a5f]">Privacy Policy</h1>
          <div className="w-10" />
        </div>

        <div className="px-5 py-6 max-w-4xl mx-auto pb-20">
          <div className="bg-white rounded-3xl p-8 shadow-lg mb-6">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-3">
              Privacy Policy
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Last Updated: November 2025
            </p>
            <p className="text-gray-600 leading-relaxed">
              At TIFL, we take your privacy seriously. This Privacy Policy explains how we collect, use, protect, and share your personal information.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {highlights.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#2a4b73] flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1 text-sm">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-600">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            {sections.map((section, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-[#1e3a5f] mb-4">
                  {section.title}
                </h3>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-gray-700 leading-relaxed text-sm">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-gradient-to-r from-[#7FE5D3]/10 to-[#5FD3C1]/10 border border-[#7FE5D3]/30 rounded-2xl p-6">
            <h3 className="font-semibold text-[#1e3a5f] mb-2">
              Your Privacy Matters
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              We are committed to protecting your privacy and being transparent about our data practices. If you have any questions or concerns, please don't hesitate to contact us.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}